import * as React from 'react';
import {ChevronDown, Search} from 'lucide-react';
import {cn} from '@/matic/lib/utils';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type SelectProps = {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (event: {target: {value: string}}) => void;
  disabled?: boolean;
  /** Non-interactive but not visually disabled (no not-allowed cursor) */
  readOnly?: boolean;
  className?: string;
  /** The wrapper's classes: by default it fills its row; pass e.g. `inline-block w-auto`. */
  containerClassName?: string;
  /** `sm`: the compact field used inline in tables and cards (h-8, text-sm, semibold). */
  size?: 'sm' | 'md';
  /** Shown while nothing is selected, muted; without one the first option's label shows. */
  placeholder?: string;
  /** The trigger's hover text. */
  title?: string;
  optionClassName?: string;
  id?: string;
  helperTextClassName?: string;
  /** Enable search filtering in the dropdown */
  searchable?: boolean;
  /** Placeholder for the search input */
  searchPlaceholder?: string;
};

const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      className,
      containerClassName,
      size = 'md',
      placeholder,
      label,
      error,
      helperText,
      id,
      options,
      value,
      onChange,
      disabled,
      readOnly,
      optionClassName,
      helperTextClassName,
      searchable = false,
      searchPlaceholder = 'Search...',
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const selectId = id ?? generatedId;
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState('');
    const containerRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    const selectedOption = options.find((opt) => opt.value === value);
    const hasValue = Boolean(value);
    const displayLabel =
      selectedOption?.label ?? placeholder ?? options[0]?.label ?? '';
    const showingPlaceholder = !selectedOption && placeholder !== undefined;

    const filteredOptions = React.useMemo(() => {
      if (!searchable || !searchTerm) return options;
      const lower = searchTerm.toLowerCase();
      return options.filter((opt) => opt.label.toLowerCase().includes(lower));
    }, [options, searchTerm, searchable]);

    // Close on outside click
    React.useEffect(() => {
      if (!isOpen) return;
      const handleClick = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setSearchTerm('');
        }
      };

      document.addEventListener('mousedown', handleClick);
      return () => {
        document.removeEventListener('mousedown', handleClick);
      };
    }, [isOpen]);

    // Close on Escape
    React.useEffect(() => {
      if (!isOpen) return;
      const handleKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
          setSearchTerm('');
        }
      };

      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [isOpen]);

    // Auto-focus search input when dropdown opens
    React.useEffect(() => {
      if (isOpen && searchable) {
        // Small delay to ensure dropdown is rendered
        requestAnimationFrame(() => {
          searchInputRef.current?.focus();
        });
      }
    }, [isOpen, searchable]);

    const handleSelect = (optionValue: string) => {
      onChange?.({target: {value: optionValue}});
      setIsOpen(false);
      setSearchTerm('');
    };

    return (
      <div
        className={cn('relative w-full', containerClassName)}
        ref={containerRef}
      >
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'mb-1 block text-sm font-medium',
              readOnly && !hasValue ? 'text-neutral-400' : 'text-neutral-700'
            )}
          >
            {label}
          </label>
        )}
        <button
          ref={ref}
          id={selectId}
          type="button"
          onClick={() => {
            if (!disabled && !readOnly) setIsOpen(!isOpen);
          }}
          className={cn(
            'flex w-full items-center justify-between rounded-md border bg-white text-sm',
            size === 'sm'
              ? 'h-8 gap-1.5 px-2 py-1 font-semibold text-neutral-900 shadow-sm hover:border-neutral-400'
              : 'h-10 px-3 py-2',
            'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            showingPlaceholder && 'font-normal text-neutral-500',
            readOnly
              ? hasValue
                ? 'cursor-default border-neutral-300 bg-neutral-50 text-neutral-700'
                : 'cursor-default border-neutral-200 bg-neutral-50 text-neutral-400'
              : disabled
                ? 'cursor-not-allowed border-neutral-300 opacity-50'
                : 'border-neutral-300',
            error &&
              'border-error-500 focus:border-error-500 focus:ring-error-500',
            className
          )}
          disabled={disabled && !readOnly}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          {...props}
        >
          <span className="truncate text-left">{displayLabel}</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 flex-shrink-0 text-neutral-500 transition-transform',
              size === 'md' && 'ml-2',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Custom dropdown */}
        {isOpen && (
          <div
            className={cn(
              'absolute z-50 mt-1 w-full rounded-md border border-neutral-200 bg-white shadow-lg',
              size === 'sm' && 'min-w-[12rem]'
            )}
            role="listbox"
          >
            {searchable && (
              <div className="border-b border-neutral-100 p-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                    placeholder={searchPlaceholder}
                    className="w-full rounded-md border border-neutral-200 py-1.5 pl-8 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </div>
              </div>
            )}
            <div className="max-h-60 overflow-auto py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-neutral-500">
                  No matches found
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={option.disabled}
                      onClick={() => {
                        if (!option.disabled) handleSelect(option.value);
                      }}
                      className={cn(
                        'flex w-full items-center text-left text-sm transition-colors',
                        size === 'sm' ? 'px-2.5 py-1.5' : 'px-3 py-2',
                        isSelected
                          ? 'bg-primary-50 font-medium text-primary-700'
                          : 'text-neutral-700',
                        !option.disabled &&
                          !isSelected &&
                          'hover:bg-primary-50/60 hover:text-primary-700',
                        option.disabled &&
                          'cursor-not-allowed text-neutral-400',
                        optionClassName
                      )}
                    >
                      {option.label}
                      {option.disabled
                        ? ' (E signature field already present)'
                        : ''}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {error && <p className="mt-1 text-sm text-error-600">{error}</p>}
        {helperText && !error && (
          <p
            className={cn('mt-1 text-xs text-neutral-500', helperTextClassName)}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
